import { StyleSheet, Platform } from 'react-native';
import type { Theme } from '../../../src/theme/getTheme';
import { CHALLENGE_CARD_WIDE_INNER_MAX_WIDTH } from '../../../src/challenges/constants/challengeCardLayout.constants';

export function getHeroBgGradientYourMove(theme: Theme): [string, string] {
  return [theme.background.primary, theme.background.card];
}

export function getHeroBgGradientLive(theme: Theme): [string, string] {
  return [`${theme.error}18`, theme.background.card];
}

export function getHeroBgGradientDefault(theme: Theme): [string, string] {
  return [theme.background.shell, theme.background.card];
}

/** VS badge: dark fills on light cards; dark-gray fills on dark cards */
export function getVsCircleGradient(theme: Theme): [string, string] {
  const lightCards = theme.background.card.toUpperCase() === '#FFFFFF';
  if (lightCards) {
    return [theme.gray[900], theme.gray[800]];
  }
  return [theme.gray[100], theme.gray[200]];
}

export function getFloorGlowGradient(theme: Theme): [string, string] {
  return ['transparent', `${theme.primary}10`];
}

export function getHeroFooterChevronColor(theme: Theme): string {
  return theme.gray[400];
}

export const getChallengeCardHeroStyles = (theme: Theme) =>
  StyleSheet.create({
    wrapper: {
      overflow: 'hidden',
      marginHorizontal: -14,
      marginTop: -12,
      marginBottom: 0,
    },
    wrapperWithBannerAbove: {
      overflow: 'hidden',
      marginHorizontal: -14,
      marginTop: 0,
      marginBottom: 0,
    },
    gradientFill: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    },
    floorGlow: {
      position: 'absolute',
      left: 0,
      right: 0,
      bottom: 0,
      height: 30,
    },
    /** Same max width as `picksCardInner` — wide screens align VS duel with match bet columns */
    heroWideInner: {
      maxWidth: CHALLENGE_CARD_WIDE_INNER_MAX_WIDTH,
      alignSelf: 'center',
      width: '100%',
    },
    container: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      justifyContent: 'space-between',
      paddingHorizontal: 14,
      paddingTop: 10,
      minHeight: 120,
    },
    playerSide: {
      flex: 1,
      alignItems: 'flex-start',
      gap: 4,
      minWidth: 0,
    },
    playerSideRight: {
      alignItems: 'flex-end',
      minWidth: 0,
    },
    youLabel: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 5,
      backgroundColor: `${theme.primary}18`,
    },
    youLabelText: {
      fontSize: 10,
      fontWeight: '900',
      color: theme.primary,
      letterSpacing: 0.6,
    },
    oppLabel: {
      paddingHorizontal: 8,
      paddingVertical: 2,
      borderRadius: 5,
      maxWidth: '100%',
    },
    oppLabelText: {
      fontSize: 10,
      fontWeight: '900',
      letterSpacing: 0.5,
      lineHeight: 13,
      color: theme.gray[600],
      textAlign: 'right',
    },
    center: {
      alignItems: 'center',
      justifyContent: 'flex-end',
      gap: 4,
      paddingBottom: 10,
    },
    vsCircle: {
      width: 36,
      height: 36,
      borderRadius: 18,
      alignItems: 'center',
      justifyContent: 'center',
    },
    vsText: {
      fontSize: 11,
      fontWeight: '900',
      color: theme.white,
      letterSpacing: -0.5,
    },
    stakeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 3,
      paddingHorizontal: 6,
      paddingVertical: 2,
      borderRadius: 5,
      backgroundColor: `${theme.accent}22`,
      borderWidth: 1,
      borderColor: `${theme.accent}99`,
    },
    coinIcon: {
      width: 14,
      height: 14,
    },
    stakeText: {
      fontSize: 10,
      fontWeight: '800',
      color: theme.accentDark,
    },
    heroFooter: {
      alignItems: 'center',
      gap: 4,
      paddingBottom: 8,
    },
    timerInline: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 1,
      paddingVertical: 4,
      paddingHorizontal: 8,
      borderRadius: 10,
      backgroundColor: theme.timerInsetBg,
    },
    timerInlineUrgent: {
      borderWidth: 1,
      borderColor: `${theme.error}80`,
    },
    timerSegment: {
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 30,
    },
    timerNumber: {
      fontSize: 14,
      fontWeight: '800',
      letterSpacing: 1,
      color: theme.accentDark,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
    timerNumberUrgent: {
      color: theme.error,
    },
    timerLabel: {
      fontSize: 7,
      marginTop: 1,
      color: theme.timerLabelColor,
    },
    timerColon: {
      fontSize: 12,
      fontWeight: '800',
      color: theme.accentDark + '99',
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
      alignSelf: 'flex-end',
      marginBottom: 7,
    },
    timerStartedText: {
      fontSize: 12,
      fontWeight: '700',
      letterSpacing: 0.5,
      color: theme.timerLabelColor,
      fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    },
  });
